// components/EditAccountAssetModal.jsx
import React, { useState, useEffect } from "react";
import { X, Loader, Package, Search } from "lucide-react";
import apiService from "../services/api";
import { useTheme } from "../contexts/ThemeContext";

const EditAccountAssetModal = ({ asset, onClose, onSuccess }) => {
  const { isDarkMode } = useTheme();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    assetName: "",
    serialNumber: null,
    productId: null,
    quantity: 1,
    status: "Requested",
    purchaseDate: null,
    installDate: null,
    usageEndDate: null,
    price: null,
    description: null,
    orderNotes: null,
  });

  // Search states
  const [products, setProducts] = useState([]);
  const [productSearch, setProductSearch] = useState("");
  const [showProductSearch, setShowProductSearch] = useState(false);
  const [loadingProducts, setLoadingProducts] = useState(false);

  useEffect(() => {
    if (asset) {
      setFormData({
        assetName: asset.assetName || "",
        serialNumber: asset.serialNumber || null,
        productId: asset.productId || null,
        quantity: asset.quantity || 1,
        status: asset.status || "Requested",
        purchaseDate: asset.purchaseDate
          ? asset.purchaseDate.split("T")[0]
          : null,
        installDate: asset.installDate ? asset.installDate.split("T")[0] : null,
        usageEndDate: asset.usageEndDate
          ? asset.usageEndDate.split("T")[0]
          : null,
        price: asset.price || null,
        description: asset.description || null,
        orderNotes: asset.orderNotes || null,
      });

      if (asset.product) {
        setProductSearch(asset.product.productName);
      }
    }
  }, [asset]);

  const searchProducts = async (query) => {
    if (!query || query.length < 2) {
      setProducts([]);
      return;
    }

    try {
      setLoadingProducts(true);
      const response = await apiService.getProducts({
        search: query,
        limit: 10,
      });
      setProducts(response.data || []);
    } catch (error) {
      console.error("Error searching products:", error);
    } finally {
      setLoadingProducts(false);
    }
  };

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (productSearch) {
        searchProducts(productSearch);
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [productSearch]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Prepare data with proper null handling
      const submitData = {
        ...formData,
        serialNumber: formData.serialNumber?.trim() || null,
        productId: formData.productId || null,
        purchaseDate: formData.purchaseDate || null,
        installDate: formData.installDate || null,
        usageEndDate: formData.usageEndDate || null,
        price: formData.price || null,
        description: formData.description?.trim() || null,
        orderNotes: formData.orderNotes?.trim() || null,
      };

      await apiService.updateAsset(asset.id, submitData);
      onSuccess();
    } catch (error) {
      console.error("Error updating asset:", error);
      alert(error.response?.data?.message || "Failed to update asset");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const selectProduct = (product) => {
    setFormData((prev) => ({
      ...prev,
      productId: product.id,
    }));
    setProductSearch(product.productName);
    setShowProductSearch(false);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
      <div
        className={`rounded-lg max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col ${
          isDarkMode
            ? "bg-[#13161B] border border-gray-700"
            : "bg-white border border-gray-300"
        }`}
      >
        {/* Header */}
        <div
          className={`px-6 py-4 border-b flex items-center justify-between ${
            isDarkMode ? "border-gray-700" : "border-gray-200"
          }`}
        >
          <div className="flex items-center gap-3">
            <div
              className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                isDarkMode
                  ? "bg-purple-500/10 border border-purple-500/20"
                  : "bg-purple-100 border border-purple-200"
              }`}
            >
              <Package
                size={20}
                className={isDarkMode ? "text-purple-400" : "text-purple-600"}
              />
            </div>
            <div>
              <h2
                className={`text-lg font-semibold ${
                  isDarkMode ? "text-white" : "text-gray-900"
                }`}
              >
                Edit Asset
              </h2>
              <p
                className={`text-sm ${
                  isDarkMode ? "text-gray-400" : "text-gray-500"
                }`}
              >
                {asset.assetName}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className={`transition-colors ${
              isDarkMode
                ? "text-gray-400 hover:text-white"
                : "text-gray-500 hover:text-gray-700"
            }`}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto p-6">
          <form
            onSubmit={handleSubmit}
            id="editAssetForm"
            className="space-y-6"
          >
            {/* Basic Information */}
            <div className="space-y-6">
              <div
                className={`flex items-center gap-3 pb-4 border-b ${
                  isDarkMode ? "border-gray-700" : "border-gray-200"
                }`}
              >
                <div
                  className={`w-1 h-5 rounded-full ${
                    isDarkMode ? "bg-purple-500" : "bg-purple-600"
                  }`}
                ></div>
                <h3
                  className={`text-base font-semibold ${
                    isDarkMode ? "text-white" : "text-gray-900"
                  }`}
                >
                  Basic Information
                </h3>
              </div>
              <div className="grid grid-cols-2 gap-6">
                <div className="col-span-2">
                  <label
                    className={`block text-sm font-medium mb-2 ${
                      isDarkMode ? "text-gray-400" : "text-gray-600"
                    }`}
                  >
                    <span className="text-red-500">*</span> Asset Name
                  </label>
                  <input
                    type="text"
                    name="assetName"
                    value={formData.assetName}
                    onChange={handleChange}
                    required
                    className={`w-full px-4 py-2 border rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all duration-200 ${
                      isDarkMode
                        ? "bg-[#0C0E12] border-gray-700 text-white placeholder-gray-500"
                        : "bg-white border-gray-300 text-gray-900 placeholder-gray-400"
                    }`}
                  />
                </div>

                <div>
                  <label
                    className={`block text-sm font-medium mb-2 ${
                      isDarkMode ? "text-gray-400" : "text-gray-600"
                    }`}
                  >
                    Serial Number
                  </label>
                  <input
                    type="text"
                    name="serialNumber"
                    value={formData.serialNumber || ""}
                    onChange={handleChange}
                    placeholder="SN-12345"
                    className={`w-full px-4 py-2 border rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all duration-200 ${
                      isDarkMode
                        ? "bg-[#0C0E12] border-gray-700 text-white placeholder-gray-500"
                        : "bg-white border-gray-300 text-gray-900 placeholder-gray-400"
                    }`}
                  />
                </div>

                <div>
                  <label
                    className={`block text-sm font-medium mb-2 ${
                      isDarkMode ? "text-gray-400" : "text-gray-600"
                    }`}
                  >
                    Status
                  </label>
                  <select
                    name="status"
                    value={formData.status}
                    onChange={handleChange}
                    className={`w-full px-4 py-2 border rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all duration-200 ${
                      isDarkMode
                        ? "bg-[#0C0E12] border-gray-700 text-white"
                        : "bg-white border-gray-300 text-gray-900"
                    }`}
                  >
                    <option value="Requested">Requested</option>
                    <option value="Ordered">Ordered</option>
                    <option value="Original Sale">Original Sale</option>
                    <option value="Add-on">Add-on</option>
                  </select>
                </div>

                {/* Product Search */}
                <div className="col-span-2 relative">
                  <label
                    className={`block text-sm font-medium mb-2 ${
                      isDarkMode ? "text-gray-400" : "text-gray-600"
                    }`}
                  >
                    Product
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      value={productSearch}
                      onChange={(e) => {
                        setProductSearch(e.target.value);
                        setShowProductSearch(true);
                      }}
                      onFocus={() => setShowProductSearch(true)}
                      placeholder="Search for a product..."
                      className={`w-full px-4 py-2 pl-10 border rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all duration-200 ${
                        isDarkMode
                          ? "bg-[#0C0E12] border-gray-700 text-white placeholder-gray-500"
                          : "bg-white border-gray-300 text-gray-900 placeholder-gray-400"
                      }`}
                    />
                    <Search
                      className={`absolute left-3 top-1/2 -translate-y-1/2 ${
                        isDarkMode ? "text-gray-500" : "text-gray-400"
                      }`}
                      size={16}
                    />
                  </div>

                  {showProductSearch && productSearch && (
                    <div
                      className={`absolute top-full mt-2 w-full rounded-xl shadow-2xl max-h-48 overflow-y-auto z-20 border ${
                        isDarkMode
                          ? "bg-[#0C0E12] border-gray-700"
                          : "bg-white border-gray-200"
                      }`}
                    >
                      {loadingProducts ? (
                        <div
                          className={`p-4 text-center ${
                            isDarkMode ? "text-gray-400" : "text-gray-500"
                          }`}
                        >
                          <Loader size={20} className="animate-spin mx-auto" />
                        </div>
                      ) : products.length > 0 ? (
                        products.map((product) => (
                          <button
                            key={product.id}
                            type="button"
                            onClick={() => selectProduct(product)}
                            className={`w-full px-4 py-3 text-left transition-colors border-b last:border-b-0 ${
                              isDarkMode
                                ? "hover:bg-gray-800/50 border-gray-800/50"
                                : "hover:bg-gray-50 border-gray-100"
                            }`}
                          >
                            <div
                              className={`font-medium text-sm ${
                                isDarkMode ? "text-white" : "text-gray-900"
                              }`}
                            >
                              {product.productName}
                            </div>
                            <div
                              className={`text-xs mt-0.5 ${
                                isDarkMode ? "text-gray-500" : "text-gray-400"
                              }`}
                            >
                              {product.productCode}
                            </div>
                          </button>
                        ))
                      ) : (
                        <div
                          className={`p-4 text-center text-sm ${
                            isDarkMode ? "text-gray-500" : "text-gray-400"
                          }`}
                        >
                          No products found
                        </div>
                      )}
                    </div>
                  )}
                </div>

                <div>
                  <label
                    className={`block text-sm font-medium mb-2 ${
                      isDarkMode ? "text-gray-400" : "text-gray-600"
                    }`}
                  >
                    <span className="text-red-500">*</span> Quantity
                  </label>
                  <input
                    type="number"
                    name="quantity"
                    value={formData.quantity}
                    onChange={handleChange}
                    min="1"
                    required
                    className={`w-full px-4 py-2 border rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all duration-200 ${
                      isDarkMode
                        ? "bg-[#0C0E12] border-gray-700 text-white"
                        : "bg-white border-gray-300 text-gray-900"
                    }`}
                  />
                </div>

                <div>
                  <label
                    className={`block text-sm font-medium mb-2 ${
                      isDarkMode ? "text-gray-400" : "text-gray-600"
                    }`}
                  >
                    Price
                  </label>
                  <input
                    type="number"
                    name="price"
                    value={formData.price || ""}
                    onChange={handleChange}
                    step="0.01"
                    min="0"
                    placeholder="0.00"
                    className={`w-full px-4 py-2 border rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all duration-200 ${
                      isDarkMode
                        ? "bg-[#0C0E12] border-gray-700 text-white placeholder-gray-500"
                        : "bg-white border-gray-300 text-gray-900 placeholder-gray-400"
                    }`}
                  />
                </div>
              </div>
            </div>

            {/* Dates */}
            <div className="space-y-6">
              <div
                className={`flex items-center gap-3 pb-4 border-b ${
                  isDarkMode ? "border-gray-700" : "border-gray-200"
                }`}
              >
                <div
                  className={`w-1 h-5 rounded-full ${
                    isDarkMode ? "bg-purple-500" : "bg-purple-600"
                  }`}
                ></div>
                <h3
                  className={`text-base font-semibold ${
                    isDarkMode ? "text-white" : "text-gray-900"
                  }`}
                >
                  Important Dates
                </h3>
              </div>
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <label
                    className={`block text-sm font-medium mb-2 ${
                      isDarkMode ? "text-gray-400" : "text-gray-600"
                    }`}
                  >
                    Purchase Date
                  </label>
                  <input
                    type="date"
                    name="purchaseDate"
                    value={formData.purchaseDate || ""}
                    onChange={handleChange}
                    className={`w-full px-4 py-2 border rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all duration-200 ${
                      isDarkMode
                        ? "bg-[#0C0E12] border-gray-700 text-white"
                        : "bg-white border-gray-300 text-gray-900"
                    }`}
                  />
                </div>

                <div>
                  <label
                    className={`block text-sm font-medium mb-2 ${
                      isDarkMode ? "text-gray-400" : "text-gray-600"
                    }`}
                  >
                    Install Date
                  </label>
                  <input
                    type="date"
                    name="installDate"
                    value={formData.installDate || ""}
                    onChange={handleChange}
                    className={`w-full px-4 py-2 border rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all duration-200 ${
                      isDarkMode
                        ? "bg-[#0C0E12] border-gray-700 text-white"
                        : "bg-white border-gray-300 text-gray-900"
                    }`}
                  />
                </div>

                <div className="col-span-2">
                  <label
                    className={`block text-sm font-medium mb-2 ${
                      isDarkMode ? "text-gray-400" : "text-gray-600"
                    }`}
                  >
                    Usage End Date
                  </label>
                  <input
                    type="date"
                    name="usageEndDate"
                    value={formData.usageEndDate || ""}
                    onChange={handleChange}
                    className={`w-full px-4 py-2 border rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all duration-200 ${
                      isDarkMode
                        ? "bg-[#0C0E12] border-gray-700 text-white"
                        : "bg-white border-gray-300 text-gray-900"
                    }`}
                  />
                </div>
              </div>
            </div>

            {/* Description */}
            <div className="space-y-6">
              <div
                className={`flex items-center gap-3 pb-4 border-b ${
                  isDarkMode ? "border-gray-700" : "border-gray-200"
                }`}
              >
                <div
                  className={`w-1 h-5 rounded-full ${
                    isDarkMode ? "bg-purple-500" : "bg-purple-600"
                  }`}
                ></div>
                <h3
                  className={`text-base font-semibold ${
                    isDarkMode ? "text-white" : "text-gray-900"
                  }`}
                >
                  Description
                </h3>
              </div>
              <textarea
                name="description"
                value={formData.description || ""}
                onChange={handleChange}
                rows={4}
                className={`w-full px-4 py-2 border rounded-xl resize-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all duration-200 ${
                  isDarkMode
                    ? "bg-[#0C0E12] border-gray-700 text-white placeholder-gray-500"
                    : "bg-white border-gray-300 text-gray-900 placeholder-gray-400"
                }`}
                placeholder="Asset description..."
              />
            </div>

            {/* Order Notes */}
            <div className="space-y-6">
              <div
                className={`flex items-center gap-3 pb-4 border-b ${
                  isDarkMode ? "border-gray-700" : "border-gray-200"
                }`}
              >
                <div
                  className={`w-1 h-5 rounded-full ${
                    isDarkMode ? "bg-purple-500" : "bg-purple-600"
                  }`}
                ></div>
                <h3
                  className={`text-base font-semibold ${
                    isDarkMode ? "text-white" : "text-gray-900"
                  }`}
                >
                  Order Notes
                </h3>
              </div>
              <textarea
                name="orderNotes"
                value={formData.orderNotes || ""}
                onChange={handleChange}
                rows={4}
                className={`w-full px-4 py-2 border rounded-xl resize-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all duration-200 ${
                  isDarkMode
                    ? "bg-[#0C0E12] border-gray-700 text-white placeholder-gray-500"
                    : "bg-white border-gray-300 text-gray-900 placeholder-gray-400"
                }`}
                placeholder="Order-related notes..."
              />
            </div>
          </form>
        </div>

        {/* Footer Actions */}
        <div
          className={`px-6 py-4 border-t flex justify-end gap-3 ${
            isDarkMode
              ? "border-gray-700 bg-[#0C0E12]"
              : "border-gray-200 bg-gray-50"
          }`}
        >
          <button
            type="button"
            onClick={onClose}
            className={`px-4 py-2 text-sm font-medium border rounded transition-colors ${
              isDarkMode
                ? "text-gray-300 bg-[#13161B] border-gray-700 hover:bg-gray-700"
                : "text-gray-700 bg-white border-gray-300 hover:bg-gray-50"
            }`}
          >
            Cancel
          </button>
          <button
            type="submit"
            form="editAssetForm"
            disabled={loading}
            className="px-4 py-2 text-sm font-medium text-white bg-purple-600 rounded hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? (
              <>
                <Loader className="inline animate-spin mr-2" size={16} />
                Saving...
              </>
            ) : (
              "Save Changes"
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditAccountAssetModal;
